import {forwardRef, useMemo, useState, type CSSProperties, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {UsersRound} from 'lucide-react';
import {ActionRow, Avatar, Button, SectionLabel} from '@chekinapp/ui';
import {buildRosterSections, type RosterEntry} from 'utils/guestRoster';
import {useGuestGroup, useGuestNavigation} from 'hooks';
import {AddSlotButton} from './AddSlotButton';
import {GuestRosterRow} from './GuestRosterRow';

type Density = 'regular' | 'compact' | 'dense';

const COMPACT_FROM = 6;
const DENSE_FROM = 10;
const FOLD_MIN_REGISTERED = 3;

const DENSITY_VARS: Record<Density, Record<string, string>> = {
  regular: {
    '--roster-stack-gap': '8px',
    '--roster-section-mt': '16px',
    '--roster-avatar': '40px',
    '--roster-avatar-font': '12px',
    '--roster-chip-mt': '2px',
    '--roster-btn-h': '30px',
    '--roster-btn-px': '10px',
    '--roster-btn-fs': '12px',
    '--action-row-px': '12px',
    '--action-row-py': '12px',
    '--action-row-gap': '12px',
    '--action-row-title-font-size': '14px',
    '--add-slot-min-height': '42px',
  },
  compact: {
    '--roster-stack-gap': '6px',
    '--roster-section-mt': '16px',
    '--roster-avatar': '32px',
    '--roster-avatar-font': '12px',
    '--roster-chip-mt': '5px',
    '--roster-btn-h': '30px',
    '--roster-btn-px': '10px',
    '--roster-btn-fs': '12px',
    '--action-row-px': '10px',
    '--action-row-py': '8px',
    '--action-row-gap': '10px',
    '--action-row-title-font-size': '14px',
    '--add-slot-min-height': '42px',
  },
  dense: {
    '--roster-stack-gap': '6px',
    '--roster-section-mt': '16px',
    '--roster-avatar': '32px',
    '--roster-avatar-font': '12px',
    '--roster-chip-mt': '2px',
    '--roster-btn-h': '30px',
    '--roster-btn-px': '10px',
    '--roster-btn-fs': '12px',
    '--action-row-px': '10px',
    '--action-row-py': '6px',
    '--action-row-gap': '10px',
    '--action-row-title-font-size': '14px',
    '--add-slot-min-height': '42px',
  },
};

const ROOT_VARS = {
  '--action-row-radius': 'var(--card-radius)',
  '--action-row-border': 'var(--card-border)',
  '--section-label-tracking': '0.06em',
};

function getDensity(total: number): Density {
  if (total >= DENSE_FROM) return 'dense';
  if (total >= COMPACT_FROM) return 'compact';
  return 'regular';
}

type Props = {
  onRegisterNew: (isLeader: boolean) => void;
  onRegisterMinor?: () => void;
  showMinors?: boolean;
  minorsAge?: number;
  onChangeGuestCount?: () => void;
};

type SectionProps = {
  title: string;
  children: ReactNode;
};

function RosterSection({title, children}: SectionProps) {
  return (
    <section className="mt-[var(--roster-section-mt)] first:mt-0">
      <SectionLabel as="h3" size="lg" className="mx-0.5 mb-2">
        {title}
      </SectionLabel>
      <div className="flex flex-col gap-[var(--roster-stack-gap)]">{children}</div>
    </section>
  );
}

type FoldProps = {
  count: number;
  isOpen: boolean;
  onToggle: () => void;
};

function RegisteredFold({count, isOpen, onToggle}: FoldProps) {
  const {t} = useTranslation();

  return (
    <ActionRow
      data-roster-row=""
      interactive={false}
      hideChevron
      leading={
        <Avatar
          shape="square"
          tone="success"
          fallback={String(count)}
          className="size-[var(--roster-avatar)] text-[length:var(--roster-avatar-font)] [&_*]:font-bold"
        />
      }
      title={t('n_guests_registered_fold', {count})}
      description={
        <span className="text-[12px] text-[color:var(--chekin-text-secondary)]">
          {isOpen ? t('hide_the_list') : t('show_the_list')}
        </span>
      }
      trailing={
        <Button
          variant="secondary"
          size="sm"
          aria-expanded={isOpen}
          onClick={onToggle}
          className="h-auto min-h-[var(--roster-btn-h)] rounded-xl px-[var(--roster-btn-px)] text-[length:var(--roster-btn-fs)] font-semibold text-[color:var(--chekin-color-brand-blue)]"
        >
          {isOpen ? t('hide') : t('show')}
        </Button>
      }
    />
  );
}

const GuestRoster = forwardRef<HTMLDivElement, Props>(
  ({onRegisterNew, onRegisterMinor, showMinors, minorsAge, onChangeGuestCount}, ref) => {
    const {t} = useTranslation();
    const {guestGroup, missingGuests} = useGuestGroup();
    const {getGuestUpdatePath} = useGuestNavigation();
    const [isFoldOpen, setIsFoldOpen] = useState(false);

    const total = guestGroup?.known_number_of_guests ?? 0;
    const density = getDensity(total);
    const canFold = total >= DENSE_FROM;
    const showOtherGuests = total > 1;

    const {holder, others} = useMemo(
      () =>
        buildRosterSections({
          members: guestGroup?.members,
          leaderId: guestGroup?.leader_id,
          missingGuests,
        }),
      [guestGroup?.members, guestGroup?.leader_id, missingGuests],
    );

    const renderEntry = (entry: RosterEntry, isLeader: boolean) => {
      if (entry.kind === 'empty') {
        return (
          <AddSlotButton
            key={`slot-${entry.ordinal}`}
            data-roster-row=""
            onClick={() => onRegisterNew(isLeader)}
          >
            {isLeader ? t('add_booking_holder') : t('add_guest_n', {n: entry.ordinal})}
          </AddSlotButton>
        );
      }

      return (
        <GuestRosterRow
          key={entry.guest.id}
          guest={entry.guest}
          isLeader={isLeader}
          getEditPath={getGuestUpdatePath}
        />
      );
    };

    const registeredOthers = others.filter(
      entry => entry.kind === 'guest' && entry.state === 'registered',
    );
    const isFolded = canFold && registeredOthers.length >= FOLD_MIN_REGISTERED;
    const visibleOthers = isFolded
      ? others.filter(entry => !registeredOthers.includes(entry))
      : others;
    const othersTitle =
      others.length > 1 ? `${t('other_guests')} · ${others.length}` : t('other_guests');

    return (
      <div
        ref={ref}
        data-density={density}
        className="flex w-full flex-col"
        style={{...ROOT_VARS, ...DENSITY_VARS[density]} as CSSProperties}
      >
        <RosterSection title={t('booking_holder')}>
          {renderEntry(holder, true)}
        </RosterSection>

        {showOtherGuests && (
          <RosterSection title={othersTitle}>
            {visibleOthers.map(entry => renderEntry(entry, false))}
            {isFolded && (
              <>
                <RegisteredFold
                  count={registeredOthers.length}
                  isOpen={isFoldOpen}
                  onToggle={() => setIsFoldOpen(open => !open)}
                />
                {isFoldOpen && registeredOthers.map(entry => renderEntry(entry, false))}
              </>
            )}
          </RosterSection>
        )}

        {onChangeGuestCount && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onChangeGuestCount}
            className="mt-5 self-center text-[14px] font-semibold text-[color:var(--chekin-color-brand-blue)] [&_svg]:size-4"
          >
            <UsersRound aria-hidden />
            {t('change_guest_count')}
          </Button>
        )}

        {showMinors && (
          <RosterSection title={t('minors_under_age', {age: minorsAge})}>
            <AddSlotButton
              data-roster-row=""
              hint={t('minor_registration_hint')}
              onClick={onRegisterMinor}
            >
              {t('add_minor_guest')}
            </AddSlotButton>
          </RosterSection>
        )}
      </div>
    );
  },
);
GuestRoster.displayName = 'GuestRoster';

export {GuestRoster};
